import { type KeyboardEvent, useEffect, useId, useRef } from "react";
import { useT } from "~/i18n";

export type OperationTraceStep = {
  origin: string;
  state: string;
  next: string;
};

/**
 * Honest path through an operational listing. Values come from existing
 * product facts, never from invented rates or decorative motion.
 */
export function OperationTrace({ origin, state, next }: OperationTraceStep) {
  const t = useT();

  return (
    <ol className="operation-trace">
      <li>
        <span>{t("trace.origin")}</span>
        <strong>{origin}</strong>
      </li>
      <li>
        <span>{t("trace.state")}</span>
        <strong>{state}</strong>
      </li>
      <li>
        <span>{t("trace.next")}</span>
        <strong>{next}</strong>
      </li>
    </ol>
  );
}

/**
 * The trace is reference, not work: it stays behind a quiet "Como funciona"
 * disclosure beside the page title so the first viewport starts with data.
 * Native <details> keeps it keyboard reachable; Escape and an outside click
 * close it and return focus to the trigger.
 */
export function OperationTraceHelp(step: OperationTraceStep) {
  const t = useT();
  const ref = useRef<HTMLDetailsElement>(null);
  const summaryRef = useRef<HTMLElement>(null);
  const panelId = useId();

  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      const node = ref.current;
      if (!node?.open) return;
      if (event.target instanceof Node && node.contains(event.target)) return;
      node.open = false;
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, []);

  const onKeyDown = (event: KeyboardEvent<HTMLDetailsElement>) => {
    const node = ref.current;
    if (event.key !== "Escape" || !node?.open) return;
    event.preventDefault();
    event.stopPropagation();
    node.open = false;
    summaryRef.current?.focus();
  };

  return (
    <details className="page-help" ref={ref} onKeyDown={onKeyDown}>
      <summary className="page-help__trigger" ref={summaryRef} aria-controls={panelId}>
        <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
          <circle cx="8" cy="8" r="6.25" />
          <path d="M6.4 6.3a1.7 1.7 0 0 1 3.3.5c0 1.1-1.7 1.4-1.7 2.4" />
          <path d="M8 11.3h.01" />
        </svg>
        <span>{t("trace.howItWorks")}</span>
      </summary>
      <div className="page-help__panel" id={panelId}>
        <OperationTrace {...step} />
      </div>
    </details>
  );
}
